import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Leaf, Award, Shield } from "lucide-react";
import logoAms from "@/assets/logo-ams-white.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-accent text-accent-foreground" role="contentinfo">
      {/* Trust badges */}
      <div className="border-b border-accent-foreground/10">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-accent-foreground/10 flex items-center justify-center">
                <Leaf className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium">100% Éco-responsable</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-accent-foreground/10 flex items-center justify-center">
                <Award className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium">Certifié FSC®</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-accent-foreground/10 flex items-center justify-center">
                <Shield className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium">ISO 9001:2015</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-accent-foreground/10 flex items-center justify-center">
                <Clock className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium">Livraison rapide</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Logo & Description */}
          <div className="space-y-4">
            <img 
              src={logoAms} 
              alt="AMS DISTRIB - Emballage alimentaire durable" 
              className="h-20 w-auto object-contain"
              width="160"
              height="80"
            />
            <p className="text-accent-foreground/70 text-sm leading-relaxed">
              <strong>AMS DISTRIB</strong>, fournisseur d'emballage alimentaire durable à Bordeaux, Toulouse et Paris. 
              Produits certifiés FSC, respectueux de l'environnement et conformes aux normes européennes.
            </p>
            <div className="flex items-center gap-2 text-sm text-accent-foreground/70">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span>Bordeaux · Toulouse · Paris</span>
            </div>
          </div>

          {/* Navigation */}
          <nav aria-label="Navigation footer">
            <h3 className="font-display text-lg font-semibold mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/produits" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm">
                  Catalogue produits
                </Link>
              </li>
              <li>
                <Link to="/produits/bols-kraft" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm">
                  Bols kraft
                </Link>
              </li>
              <li>
                <Link to="/produits/gobelets-kraft" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm">
                  Gobelets carton
                </Link>
              </li>
              <li>
                <Link to="/valeurs" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm">
                  Nos valeurs
                </Link>
              </li>
              <li>
                <Link to="/certifications" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm">
                  Certifications
                </Link>
              </li>
              <li>
                <Link to="/devis" className="text-accent-foreground/70 hover:text-accent-foreground transition-colors text-sm font-medium">
                  Demander un devis
                </Link>
              </li>
            </ul>
          </nav>

          {/* Contact */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:contact@amsdistrib.fr" 
                  className="flex items-center gap-2 text-sm text-accent-foreground/70 hover:text-accent-foreground transition-colors"
                  aria-label="Envoyer un email à contact@amsdistrib.fr"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  contact@amsdistrib.fr
                </a>
              </li>
              <li>
                <a 
                  href="mailto:servicecommercial@amsdistrib.fr" 
                  className="flex items-center gap-2 text-sm text-accent-foreground/70 hover:text-accent-foreground transition-colors"
                  aria-label="servicecommercial@amsdistrib.fr - Envoyer un email au service commercial"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  servicecommercial@amsdistrib.fr
                </a>
              </li>
              <li>
                <a 
                  href="tel:+33611416100" 
                  className="flex items-center gap-2 text-sm text-accent-foreground/70 hover:text-accent-foreground transition-colors"
                  aria-label="Appeler le 06 11 41 61 00"
                >
                  <Phone className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  06 11 41 61 00
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-accent-foreground/70 pt-2">
                <Clock className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                Lun - Ven : 9h - 18h
              </li>
            </ul>
          </div>

          {/* Certifications & Products */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-4">Nos produits</h3>
            <ul className="space-y-2 text-sm text-accent-foreground/70 mb-6">
              <li>Bols et saladiers kraft</li>
              <li>Gobelets carton et rPET</li>
              <li>Sacs kraft personnalisables</li>
              <li>Barquettes et contenants</li>
              <li>Pailles biodégradables</li>
              <li>Consommables hygiène</li>
            </ul>
            <h4 className="font-medium text-sm mb-2">Certifications</h4>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-accent-foreground/10 rounded-full text-xs font-medium">FSC®</span>
              <span className="px-3 py-1 bg-accent-foreground/10 rounded-full text-xs font-medium">ISO 9001</span>
              <span className="px-3 py-1 bg-accent-foreground/10 rounded-full text-xs font-medium">Normes UE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-accent-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-accent-foreground/50">
            <p>© {currentYear} AMS DISTRIB. Tous droits réservés.</p>
            <div className="flex items-center gap-6">
              <nav aria-label="Liens légaux" className="flex gap-6">
                <Link to="/valeurs" className="hover:text-accent-foreground transition-colors">
                  Mentions légales
                </Link>
                <Link to="/valeurs" className="hover:text-accent-foreground transition-colors">
                  Politique de confidentialité
                </Link>
              </nav>
              <Link
                to="/admin/login"
                className="px-4 py-1.5 rounded-full bg-accent-foreground/10 hover:bg-accent-foreground/20 text-accent-foreground/60 hover:text-accent-foreground text-xs font-medium transition-colors"
              >
                Administration
              </Link>
            </div>
          </div>
          <div className="text-center mt-4 text-sm text-accent-foreground/50">
            <a 
              href="https://linkedin.com/company/ams-distrib" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-accent-foreground transition-colors underline"
            >
              AMS DISTRIB
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;